<template>
  <div class="mb-7 mt-48">
    <slot
      :current-step
      name="sticky-bar"
      :title="selectedTitle"
      :total-steps="funnel.length"
    />

    <aside v-for="step in funnel" :key="step.id" class="mb-48">
      <Step :pickers :product :step />
    </aside>

    <ErrorBoundary :owners="['bot-squad-b2cservices-front']">
      <RakutenStep class="mb-48" :pickers :product />
    </ErrorBoundary>
  </div>

  <ClientOnly>
    <NewBatteryModal
      :brand="product.brand"
      :category-id="product.tracking.categoryId"
      :device-name="product.titles.raw"
    />
    <SimModal v-if="showSimModal" />
    <ProcessorModal v-if="showProcessorModal" />
    <ConditionModal v-if="funnel[0]" :brand="product.brand" :step="funnel[0]" />
  </ClientOnly>
</template>

<script lang="ts" setup>
import { useState } from '#imports'
import { computed } from 'vue'

import type { Price } from '@backmarket/http-api'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import ErrorBoundary from '@backmarket/nuxt-module-logger/components/ErrorBoundary.vue'

import NewBatteryModal from '~/scopes/product/components/CustomizationFunnel/components/NewBatteryModal/NewBatteryModal.vue'
import ProcessorModal from '~/scopes/product/components/CustomizationFunnel/components/ProcessorModal/ProcessorModal.vue'
import SimModal from '~/scopes/product/components/CustomizationFunnel/components/SimModal/SimModal.vue'
import { useProductTracking } from '~/scopes/product/composables/useProductTracking'

import ConditionModal from '../ConditionModal/ConditionModal.vue'

import RakutenStep from './components/RakutenStep/RakutenStep.vue'
import Step from './components/Step/Step.vue'
import { createCustomizationFunnelData } from './utils/createCustomizationFunnelData'

const props = withDefaults(
  defineProps<{
    selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
    pickers: GetPickersResponse
    isServicesPickersResponsePending: boolean
    price: Price
    priceWithoutSubsidies?: Price | null
    priceWithoutTradeIn?: Price | null
    product: GetProductResponse
    productTracking: ReturnType<typeof useProductTracking>
  }>(),
  {
    priceWithoutSubsidies: undefined,
    priceWithoutTradeIn: null,
  },
)

const i18n = useI18n()
const experiments = useExperiments()
const currentStep = useState('current-step', () => 0)

const funnel = computed(() => {
  return createCustomizationFunnelData(
    i18n,
    props.pickers.pickerGroups,
    experiments,
  )
})

const selectedOptions = computed(() => {
  const variants = funnel.value
    ?.reduce<Array<string>>((acc, { options, type }) => {
      if (type !== 'service') {
        const label =
          options?.find(({ selected }) => !!selected)?.label ?? undefined
        if (label) {
          acc.push(typeof label === 'object' ? i18n(label) : label)
        }
      }

      return acc
    }, [])
    .filter(Boolean)

  return variants
})

const selectedTitle = computed(() => {
  return selectedOptions.value.join(' - ')
})

const showSimModal = computed(() => {
  return props.pickers.pickerGroups?.some(({ id }) => id === 'dual_sim')
})

const showProcessorModal = computed(() => {
  return props.pickers.pickerGroups?.some(
    ({ id }) =>
      id === 'processor_type_and_speed' ||
      id === 'processor_type_and_graphic_card',
  )
})
</script>
